const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID as string | undefined

// Extend window type for Facebook Pixel
declare global {
  interface Window {
    fbq: (
      action: string,
      eventOrPixelId: string,
      params?: Record<string, unknown>
    ) => void
    _fbq: unknown
  }
}

/**
 * Initialize Facebook Pixel
 * Call this once in main.tsx
 */
export const initFacebookPixel = () => {
  if (!FB_PIXEL_ID) {
    if (import.meta.env.DEV) {
      console.warn('Facebook Pixel ID not configured. Set VITE_FB_PIXEL_ID in .env')
    }
    return
  }

  // Facebook Pixel base code
  const b = document
  const e = 'script'

  // Check if already initialized (fbq will have loaded property if initialized)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (window.fbq && (window.fbq as any).loaded) return

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n: any = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args)
  }

  if (!window._fbq) window._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  window.fbq = n

  const t = b.createElement(e) as HTMLScriptElement
  t.async = true
  t.src = 'https://connect.facebook.net/en_US/fbevents.js'

  const s = b.getElementsByTagName(e)[0]
  s?.parentNode?.insertBefore(t, s)

  // Initialize pixel
  window.fbq('init', FB_PIXEL_ID)
  window.fbq('track', 'PageView')

  console.log('Facebook Pixel initialized:', FB_PIXEL_ID)
}

/**
 * Check if Facebook Pixel is initialized
 */
export const isFBPixelInitialized = (): boolean => !!FB_PIXEL_ID && !!window.fbq

// ============ Page Tracking ============

/**
 * Track page view
 */
export const fbTrackPageView = () => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', 'PageView')
}

// ============ Authentication Events ============

/**
 * Track user registration (CompleteRegistration is a standard FB event)
 */
export const fbTrackSignup = (method: 'email' | 'phone' | 'google') => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', 'CompleteRegistration', {
    content_name: 'signup',
    method: method,
  })
}

/**
 * Track user login (custom event)
 */
export const fbTrackLogin = (method: 'email' | 'phone' | 'google') => {
  if (!isFBPixelInitialized()) return
  window.fbq('trackCustom', 'Login', {
    method: method,
  })
}

// ============ Test Events ============

/**
 * Track test start (ViewContent is a standard FB event)
 */
export const fbTrackTestStart = (testCode: string, testName?: string) => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', 'ViewContent', {
    content_type: 'test',
    content_ids: [testCode],
    content_name: testName || testCode,
  })
  window.fbq('trackCustom', 'StartTest', {
    test_code: testCode,
    test_name: testName,
  })
}

/**
 * Track test completion (custom event)
 */
export const fbTrackTestComplete = (
  testCode: string,
  score: number,
  totalQuestions: number,
  attemptedQuestions: number
) => {
  if (!isFBPixelInitialized()) return
  window.fbq('trackCustom', 'CompleteTest', {
    test_code: testCode,
    score: score,
    total_questions: totalQuestions,
    attempted_questions: attemptedQuestions,
    completion_rate: totalQuestions > 0 ? Math.round((attemptedQuestions / totalQuestions) * 100) : 0,
  })
}

/**
 * Track test timeout
 */
export const fbTrackTestTimeout = (testCode: string) => {
  if (!isFBPixelInitialized()) return
  window.fbq('trackCustom', 'TestTimeout', {
    test_code: testCode,
  })
}

// ============ Lead Generation ============

/**
 * Track lead generation (standard FB event - useful for remarketing)
 */
export const fbTrackLead = (contentName?: string) => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', 'Lead', {
    content_name: contentName || 'neet_preparation',
  })
}

// ============ Payment Events ============

/**
 * Track checkout initiation (InitiateCheckout is a standard FB event)
 */
export const fbTrackInitiateCheckout = (amount: number, currency: string = 'INR') => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', 'InitiateCheckout', {
    value: amount,
    currency: currency,
    content_name: 'NEET Premium Subscription',
  })
}

/**
 * Track successful purchase (Purchase is a standard FB event)
 */
export const fbTrackPurchase = (orderId: string, amount: number, currency: string = 'INR') => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', 'Purchase', {
    value: amount,
    currency: currency,
    content_name: 'NEET Premium Subscription',
    content_type: 'subscription',
    order_id: orderId,
  })
}

// ============ Content View Events ============

/**
 * Track PYQ content view (ViewContent is a standard FB event)
 */
export const fbTrackPYQView = (contentType: 'exam' | 'topic' | 'syllabus', contentId: string, contentName?: string) => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', 'ViewContent', {
    content_type: `pyq_${contentType}`,
    content_ids: [contentId],
    content_name: contentName || contentId,
  })
}

/**
 * Track generic page/view content (ViewContent is a standard FB event)
 */
export const fbTrackViewContent = (
  contentType: string,
  contentId?: string,
  contentName?: string,
  additionalParams?: Record<string, unknown>
) => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', 'ViewContent', {
    content_type: contentType,
    content_ids: contentId ? [contentId] : undefined,
    content_name: contentName,
    ...additionalParams,
  })
}

// ============ Custom Events ============

/**
 * Track custom event
 */
export const fbTrackCustomEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (!isFBPixelInitialized()) return
  window.fbq('trackCustom', eventName, params)
}

/**
 * Track standard FB event
 */
export const fbTrackStandardEvent = (
  eventName: 'ViewContent' | 'Search' | 'AddToCart' | 'AddToWishlist' | 'InitiateCheckout' | 'Purchase' | 'Lead' | 'CompleteRegistration' | 'Subscribe' | 'Contact' | 'CustomizeProduct' | 'Donate' | 'FindLocation' | 'Schedule' | 'StartTrial' | 'SubmitApplication',
  params?: Record<string, unknown>
) => {
  if (!isFBPixelInitialized()) return
  window.fbq('track', eventName, params)
}
